import { X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { deviceService } from '@/feature/device/services/deviceService';
import type { IDeviceDetails } from '@/feature/device/types/device';
import {
  getBillingDisplayFields,
  getRentalDisplayFields,
  hasRentalSection,
  isBillingPaused,
} from '@/feature/device/utils/deviceBillingUtils';
import {
  formatDeviceMode,
  formatDevicePlatform,
  formatDeviceStatus,
  getDeviceLookupId,
} from '@/feature/device/utils/deviceLabels';
import Button from '@/shared/components/ui/Button';
import Modal from '@/shared/components/ui/Modals/Modal';
import { formatDate } from '@/shared/utils/dateUtils';

import DeviceActionsDropdown from './DeviceActionsDropdown';
import DeviceStatusBadge from './DeviceStatusBadge';
import type { IDeviceActionHandler } from './deviceActionTypes';

type DetailsTab = 'device' | 'billing';

interface IDeviceDetailsModalProps {
  isOpen: boolean;
  deviceId: string | null;
  refreshKey?: number;
  onClose: () => void;
  onAction: IDeviceActionHandler;
}

function SectionTitle({ children, first = false }: { children: ReactNode; first?: boolean }) {
  return (
    <h4
      className={`text-sm font-bold text-gray-900 m-0 mb-3.5 ${
        first ? 'pt-0 border-t-0' : 'pt-4 border-t border-gray-200'
      }`}
    >
      {children}
    </h4>
  );
}

function InfoField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className='flex flex-col gap-1'>
      <span className='text-xs font-semibold uppercase tracking-wide text-gray-500'>{label}</span>
      <span className='text-sm font-semibold text-gray-900 break-words'>{value || '—'}</span>
    </div>
  );
}

const DeviceDetailsModal = ({
  isOpen,
  deviceId,
  refreshKey = 0,
  onClose,
  onAction,
}: IDeviceDetailsModalProps) => {
  const [device, setDevice] = useState<IDeviceDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailsTab>('device');

  useEffect(() => {
    if (!isOpen || !deviceId) {
      setDevice(null);
      setActiveTab('device');
      return;
    }

    const load = async () => {
      setIsLoading(true);
      const result = await deviceService.getDeviceById(deviceId);
      setDevice(result);
      setIsLoading(false);
    };

    void load();
  }, [isOpen, deviceId, refreshKey]);

  const handleAction: IDeviceActionHandler = (action, deviceRow) => {
    onAction(action, deviceRow);
  };

  const rentalFields = device ? getRentalDisplayFields(device) : [];
  const billingFields = device ? getBillingDisplayFields(device) : [];
  const showBillingPaused = device ? isBillingPaused(device) : false;
  const showRentalBlock = device ? hasRentalSection(device) : false;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={undefined}
      size='lg'
      showCloseButton={false}
      className='max-w-[600px] !overflow-visible'
    >
      <div className='px-6 pb-6 pt-1'>
        {isLoading && <p className='text-sm text-gray-600'>Loading device details...</p>}
        {!isLoading && !device && <p className='text-sm text-red-600'>Device not found</p>}
        {!isLoading && device && (
          <>
            <div className='flex items-start justify-between gap-3 mb-1.5'>
              <h3 className='text-xl font-semibold text-gray-900 m-0 break-words'>
                {device.device_name}
              </h3>
              <button
                type='button'
                onClick={onClose}
                className='-mr-1 shrink-0 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600'
                aria-label='Close modal'
              >
                <X size={20} />
              </button>
            </div>
            <div className='flex items-center gap-2.5 mb-4 flex-wrap'>
              <span className='text-sm text-gray-500'>
                {getDeviceLookupId(device) || 'Device details and current rental information'}
              </span>
              <DeviceStatusBadge status={device.device_status} />
            </div>

            <div className='border-b border-gray-200 mb-4'>
              <nav className='flex gap-6' aria-label='Device details tabs' role='tablist'>
                <button
                  type='button'
                  role='tab'
                  aria-selected={activeTab === 'device'}
                  className={`pb-2.5 text-sm font-semibold bg-transparent border-0 border-b-2 -mb-px cursor-pointer ${
                    activeTab === 'device'
                      ? 'border-theme-500 text-theme-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab('device')}
                >
                  Device Information
                </button>
                <button
                  type='button'
                  role='tab'
                  aria-selected={activeTab === 'billing'}
                  className={`pb-2.5 text-sm font-semibold bg-transparent border-0 border-b-2 -mb-px cursor-pointer ${
                    activeTab === 'billing'
                      ? 'border-theme-500 text-theme-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab('billing')}
                >
                  Billing Information
                </button>
              </nav>
            </div>

            {activeTab === 'device' && (
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4'>
                <InfoField label='Device ID' value={getDeviceLookupId(device)} />
                <InfoField label='Device Type' value={formatDevicePlatform(device.platform)} />
                <InfoField label='Device Mode' value={formatDeviceMode(device.device_mode)} />
                <InfoField label='Real Device' value={device.real ? 'Yes' : 'No'} />
                <InfoField label='VNC URL' value={device.vnc_url} />
                <InfoField label='Appium URL' value={device.appium_url} />
                <InfoField label='Status' value={formatDeviceStatus(device.device_status)} />
                <InfoField
                  label='Added On'
                  value={formatDate(device.added_on || device.created_at)}
                />
              </div>
            )}

            {activeTab === 'billing' && (
              <>
                {showRentalBlock ? (
                  <>
                    <SectionTitle first>Rental Information</SectionTitle>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mb-4'>
                      {rentalFields.map((field) => (
                        <InfoField key={field.label} label={field.label} value={field.value} />
                      ))}
                    </div>

                    <SectionTitle>Billing</SectionTitle>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4'>
                      {billingFields.map((field) => (
                        <InfoField key={field.label} label={field.label} value={field.value} />
                      ))}
                    </div>
                    {showBillingPaused && (
                      <div className='mt-3 mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-900'>
                        Billing is paused while this device is in Maintenance.
                      </div>
                    )}
                  </>
                ) : (
                  <p className='text-sm text-gray-500 m-0'>
                    No rental or billing information for this device.
                  </p>
                )}
              </>
            )}
          </>
        )}

        <div className='flex items-center justify-end gap-3 mt-5 flex-wrap'>
          <Button type='button' variant='secondary' onClick={onClose}>
            Close
          </Button>
          {!isLoading && device ? (
            <DeviceActionsDropdown
              device={device}
              onAction={handleAction}
              openUpward
              align='right'
            />
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default DeviceDetailsModal;
